import { useCallback, useState } from "react";
import { send_request } from "../../../utils/Request";

// V2 agent functions are exposed to an agent as `agent_fn_{slug}` tools whose
// equipped state lives in the bindings collection (the runtime's source of
// truth), NOT in the agent's persisted `enabledTools`. This prefix is the
// FE-side mirror of AgentFunctionToolAdapter.TOOL_NAME_PREFIX.
export const AGENT_FUNCTION_TOOL_PREFIX = "agent_fn_";

export const isAgentFunctionTool = (toolName) =>
  typeof toolName === "string" &&
  toolName.startsWith(AGENT_FUNCTION_TOOL_PREFIX);

/**
 * Owns the agent-function equip state for the tool picker so the drawer does not
 * have to branch on the `agent_fn_` prefix across multiple handlers.
 *
 *   - `equipped`  — agent_fn_* tool names the picker should render as equipped.
 *   - `meta`      — maps each agent_fn_* name to { functionId, slug, allAgents }.
 *   - `setEquipped` / `setMeta` — populated by the config bootstrap loader.
 *   - `reset`     — clears state when the drawer closes.
 *   - `toggle`    — equip/unequip a single function (writes the binding for an
 *                   existing agent; tracked locally for a brand-new agent).
 *   - `applyPending` — persists locally-tracked bindings once a new agent exists.
 *
 * allAgents functions are tenant-wide (toggled on the function itself), so the
 * per-agent toggle is a no-op for them.
 */
export function useAgentFunctionBindings(agentId) {
  const [equipped, setEquipped] = useState([]);
  const [meta, setMeta] = useState({});

  const reset = useCallback(() => {
    setEquipped([]);
    setMeta({});
  }, []);

  const toggle = useCallback(
    async (toolName, desired) => {
      const entry = meta[toolName];
      if (!entry || entry.allAgents) return;
      // Optimistic local update; reverted below if the network write fails.
      setEquipped((prev) => {
        const has = prev.includes(toolName);
        if (desired && !has) return [...prev, toolName];
        if (!desired && has) return prev.filter((n) => n !== toolName);
        return prev;
      });
      // A brand-new agent has no id yet — keep the choice local and let
      // applyPending persist it right after the agent is created.
      if (!agentId) return;
      try {
        const path = `ai-service/agent-functions/${entry.functionId}/bindings/${agentId}`;
        const res = await send_request(path, "", {}, desired ? "POST" : "DELETE");
        if (res instanceof Error || res?.isAxiosError || res?.response) {
          throw new Error("Failed to update binding");
        }
      } catch (e) {
        setEquipped((prev) => {
          const has = prev.includes(toolName);
          if (desired && has) return prev.filter((n) => n !== toolName);
          if (!desired && !has) return [...prev, toolName];
          return prev;
        });
      }
    },
    [meta, agentId]
  );

  // After a brand-new agent is created, persist the bindings the user equipped
  // in the picker (allAgents functions are auto-available and skipped).
  // Best-effort: a failed binding does not block agent creation.
  const applyPending = useCallback(
    async (newAgentId) => {
      if (!newAgentId) return;
      const functionIds = equipped
        .map((name) => meta[name])
        .filter((m) => m && !m.allAgents && m.functionId)
        .map((m) => m.functionId);
      if (functionIds.length === 0) return;
      await Promise.all(
        functionIds.map((functionId) =>
          send_request(
            `ai-service/agent-functions/${functionId}/bindings/${newAgentId}`,
            "",
            {},
            "POST"
          ).catch(() => {})
        )
      );
    },
    [equipped, meta]
  );

  return {
    equipped,
    meta,
    setEquipped,
    setMeta,
    reset,
    toggle,
    applyPending,
    isAgentFunctionTool,
  };
}
